import { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import posthog from 'posthog-js';
import { createContext } from 'use-context-selector';

import { FeatureFlagsOptions } from '@/hooks/useFeatureFlags/types';

type FeatureFlagsProps = {
  [key in FeatureFlagsOptions]: boolean;
};

interface IFeatureFlag {
  featureFlags: FeatureFlagsProps;
  setFeatureFlags: Function;
}

export const FeatureFlagContext = createContext<IFeatureFlag>({
  featureFlags: {} as FeatureFlagsProps,
  setFeatureFlags: () => {},
});

export const FeatureFlagProvider: FC<{ children: ReactElement }> = ({ children }) => {
  // Inicializa o estado com todas as flags como `false`
  const [featureFlags, setFeatureFlags] = useState<FeatureFlagsProps>(() =>
    Object.values(FeatureFlagsOptions).reduce((acc, key) => {
      acc[key as FeatureFlagsOptions] = false;
      return acc;
    }, {} as FeatureFlagsProps),
  );

  useEffect(() => {
    const fetchFeatureFlags = () => {
      // Solução temporária: Foi acordado com o time em 28/04/2025 a não realizar chamadas ao posthog.getFeatureFlag` para as flags que estão desabilitadas no posthog devido aos custos terem sido elevados pelo consumo das ff.
      // Essa solução é temporária e deve ser revisada quando tivermos uma solução mais robusta para o gerenciamento de feature flags.
      // A solução ideal seria utilizar o `posthog.getFeatureFlag` para todas as flags, mas isso pode aumentar os custos de ff no posthog.
      // O código abaixo força as flags que estão desabilitadas no posthog a serem `false` no front-end, não necessitando chamar a função posthog.getFeatureFlag para essas flags em específico.

      const flagsDisabled = [
        FeatureFlagsOptions.FF_SAPRON_OLD_BANNER_SPOT,
        FeatureFlagsOptions.FF_SAPRON_ENABLE_OWNER_EDITION,
        FeatureFlagsOptions.FF_SAPRON_ENABLE_HOST_EDITION,
        FeatureFlagsOptions.FF_SAPRON_ENABLE_HOST_BANK_ACCOUNT_EDITION,
      ];

      const flagKeys = [
        // Lista de feature flags que deseja buscar
        FeatureFlagsOptions.FF_SAPRON_OLD_BANNER_SPOT,
        FeatureFlagsOptions.FF_SAPRON_REDIRECT_OWNER,
        FeatureFlagsOptions.FF_SAPRON_OWNER_DROPDOWN,
        FeatureFlagsOptions.FF_SAPRON_DISABLE_BANK_ACCOUNT,
        FeatureFlagsOptions.FF_SAPRON_ENABLE_OWNER_EDITION,
        FeatureFlagsOptions.FF_SAPRON_ENABLE_HOST_EDITION,
        FeatureFlagsOptions.FF_SAPRON_ENABLE_HOST_BANK_ACCOUNT_EDITION,
        FeatureFlagsOptions.FF_SAPRON_ENABLE_OWNER_PERSPECTIVE,
        FeatureFlagsOptions.FF_SAPRON_FRONT_ENABLE_PAGE_STATUS_ANUNCIO,
        FeatureFlagsOptions.FF_SAPRON_ENABLE_CLOSING_V3,
        FeatureFlagsOptions.FF_SAPRON_V2_BLOCKING_PROPERTY,
        FeatureFlagsOptions.FF_SAPRON_VISIBLE_LINK_SCORE,
        FeatureFlagsOptions.FF_SAPRON_NEW_EXPENSE_PAGE,
        FeatureFlagsOptions.FF_SAPRON_NEW_REFUND_PAGE,
        FeatureFlagsOptions.FF_SAPRON_POPUP_EXPENSE_PENDING,
      ];

      // Obtém os valores das flags
      const flags = flagKeys.reduce((acc, key) => {
        acc[key] = flagsDisabled.includes(key) ? false : !!posthog.getFeatureFlag(key);
        return acc;
      }, {} as FeatureFlagsProps);

      setFeatureFlags(flags);
    };

    fetchFeatureFlags();

    // Atualiza as flags automaticamente quando necessário
    posthog.onFeatureFlags(() => {
      fetchFeatureFlags();
    });
  }, []);

  const value = useMemo(
    () => ({
      featureFlags,
      setFeatureFlags,
    }),
    [featureFlags, setFeatureFlags],
  );

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};
